import { Link } from 'react-router-dom';
import SEO from '../components/SEO';
import PageHeader from '../components/PageHeader';
import GalleryGrid from '../components/GalleryGrid';

export default function Automation() {
  const automationSubsections = [
    {
      title: 'Automatiske maskiner',
      path: '/automation/automatiske-maskiner',
      desc: 'Udvikling og levering af automatiske maskiner og procesceller skræddersyet til at øge effektiviteten i din produktion.',
      icon: 'fa-solid fa-industry',
      tag: 'Specialmaskiner • Montage',
    },
    {
      title: 'Robot programmering',
      path: '/automation/robot-programmering',
      desc: 'Programmering af KUKA, Motoman og UR (Universal Robots) robotter. Baneplanlægning, sikkerhed og skræddersyede gribere.',
      icon: 'fa-solid fa-robot',
      tag: 'KUKA • Motoman • UR',
    },
    {
      title: 'PLC og SCADA programmering',
      path: '/automation/plc-scada',
      desc: 'Pålidelig softwarearkitektur i Siemens, Mitsubishi, Allan Bradley samt SCADA overvågning med WinCC, Indusoft og Wonderware.',
      icon: 'fa-solid fa-microchip',
      tag: 'Siemens • WinCC • Rockwell',
    },
    {
      title: 'Problemløsninger',
      path: '/problemlosninger',
      desc: 'Fejlretning, procesoptimering, afhjælpning af flaskehalse og automatisering af manuelle opgaver i eksisterende linjer.',
      icon: 'fa-solid fa-screwdriver-wrench',
      tag: 'Fejlfinding • Optimering',
    },
  ];

  const galleryImages = [
    'https://assets.auxodev.dk/assets/1cab479d-9d2b-49e7-a4f6-be9602b123ca/hero-automation-engineering.jpg',
    'https://assets.auxodev.dk/assets/1cab479d-9d2b-49e7-a4f6-be9602b123ca/automatiske-maskiner-montage.jpg',
    'https://assets.auxodev.dk/assets/1cab479d-9d2b-49e7-a4f6-be9602b123ca/elkonstruktion-styretavle.jpg',
    'https://assets.auxodev.dk/assets/1cab479d-9d2b-49e7-a4f6-be9602b123ca/problemlosning-optimering.jpg',
  ];

  return (
    <div className="bg-[#0b1017] text-slate-100 min-h-screen">
      <SEO
        title="Automation - JARCon ApS"
        description="Robot programmering af KUKA, Motoman og UR robotter. PLC (Siemens, Mitsubishi, Allan Bradley) og SCADA (WinCC, Indusoft, Wonderware mm.)."
      />

      <PageHeader
        badge="Industriel Automation"
        title="Automation"
        subtitle="Robot programmering • PLC & SCADA • Automatiske maskiner • Problemløsninger"
        breadcrumbs={[{ label: 'Automation' }]}
        ctaText="Hør om automationsløsninger"
        ctaLink="/kontakt"
      />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        {/* Scrape Content Highlights */}
        <div className="p-8 sm:p-10 rounded-xl bg-[#0e1622] border border-slate-800 mb-16 space-y-6">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-md bg-cyan-950/70 border border-cyan-500/40 text-cyan-400 font-open-sans text-xs uppercase tracking-wider">
            <span>Teknisk platform</span>
          </div>
          <h2 className="font-display text-2xl sm:text-3xl font-bold text-white">
            Programmering af industrirobotter, PLC og SCADA
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 pt-4">
            <div className="p-6 rounded-lg bg-[#080d14] border border-slate-800 space-y-3">
              <h3 className="font-display font-semibold text-lg text-white flex items-center gap-2">
                <i className="fa-solid fa-robot text-cyan-400" aria-hidden="true" />
                Robot programmering
              </h3>
              <p className="text-slate-300 text-sm">
                Programmering af KUKA, Motoman og UR robotter.
              </p>
              <p className="text-slate-400 text-xs leading-relaxed">
                Fra kollaborative UR-robotter til hurtige KUKA og Motoman 6-aksede industrirobotter sikrer vi hurtige cyklustider og høj oppetid.
              </p>
            </div>

            <div className="p-6 rounded-lg bg-[#080d14] border border-slate-800 space-y-3">
              <h3 className="font-display font-semibold text-lg text-white flex items-center gap-2">
                <i className="fa-solid fa-server text-cyan-400" aria-hidden="true" />
                PLC og SCADA programmering
              </h3>
              <p className="text-slate-300 text-sm">
                PLC: Siemens, Mitsubishi, Allan Bradley mm.<br />
                SCADA: Siemens WinCC, Indusoft, Wonderware mm.
              </p>
              <p className="text-slate-400 text-xs leading-relaxed">
                Strukturerede styringsprogrammer, operatørvenlige skærmbilleder og pålidelig dataindsamling.
              </p>
            </div>
          </div>
        </div>

        {/* Undersider Navigation Cards */}
        <div className="mb-16">
          <div className="mb-8">
            <span className="text-xs font-open-sans text-cyan-400 uppercase tracking-widest block mb-1 font-semibold">
              Undersider i sektionen
            </span>
            <h3 className="font-display text-2xl font-bold text-white">
              Vælg et specifikt automationsområde
            </h3>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            {automationSubsections.map((sub) => (
              <Link
                key={sub.title}
                to={sub.path}
                className="group flex flex-col justify-between p-8 rounded-xl bg-[#0e1622] border border-slate-800 hover:border-cyan-500/60 hover:shadow-2xl hover:shadow-cyan-950/20 transition-all duration-200"
              >
                <div>
                  <div className="flex items-center justify-between mb-4">
                    <div className="w-12 h-12 rounded-lg bg-cyan-950/70 border border-cyan-500/30 text-cyan-400 flex items-center justify-center text-xl group-hover:bg-cyan-500 group-hover:text-white transition-colors">
                      <i className={sub.icon} aria-hidden="true" />
                    </div>
                    <span className="text-[11px] font-open-sans text-slate-400 bg-slate-900 px-2.5 py-1 rounded border border-slate-800">
                      {sub.tag}
                    </span>
                  </div>
                  <h4 className="font-display text-xl font-bold text-white group-hover:text-cyan-400 transition-colors mb-2">
                    {sub.title}
                  </h4>
                  <p className="text-sm text-slate-400 leading-relaxed font-sans">
                    {sub.desc}
                  </p>
                </div>
                <div className="pt-6 mt-6 border-t border-slate-800/80 flex items-center justify-between text-xs font-open-sans text-cyan-400 font-semibold">
                  <span>Gå til undersiden</span>
                  <i className="fa-solid fa-arrow-right text-[11px] group-hover:translate-x-1 transition-transform" aria-hidden="true" />
                </div>
              </Link>
            ))}
          </div>
        </div>

        {/* Gallery */}
        <GalleryGrid
          title="Automation &amp; Robot galleri"
          subtitle="Billeder af automationsudstyr, robotceller og styring"
          images={galleryImages}
        />
      </div>
    </div>
  );
}
